/**
 * 移动端与主项目的通信工具
 * 支持游戏登录、导航、状态同步等功能
 */

export interface MessageData {
  type: string;
  payload: any;
  source: string;
  target: string;
  timestamp: number;
}

export interface GameLoginData {
  gameId: string;
  gameType: string;
  userId?: string;
  token?: string;
}

export interface NavigationData {
  path: string;
  params?: Record<string, any>;
  replace?: boolean;
}

export interface UserData {
  id: string;
  username: string;
  balance: number;
  avatar?: string;
  isVip?: boolean;
}

export class MobileCommunication {
  private listeners: Map<string, ((payload: any) => void)[]> = new Map();
  private source = "mobile-homepage";

  constructor() {
    this.init();
  }

  private init() {
    // 监听来自主应用的消息
    window.addEventListener("message", this.handleMessage.bind(this));

    // 监听来自父窗口的消息（如果嵌入在iframe中）
    window.addEventListener("message", this.handleParentMessage.bind(this));
  }

  private handleMessage = (event: MessageEvent) => {
    const { type, payload, target } = event.data as MessageData;

    if (target === this.source && this.listeners.has(type)) {
      this.listeners.get(type)!.forEach(callback => {
        try {
          callback(payload);
        } catch (error) {
          console.error(`Error in message handler for ${type}:`, error);
        }
      });
    }
  };

  private handleParentMessage = (event: MessageEvent) => {
    // 处理来自父窗口的消息
    this.handleMessage(event);
  };

  /**
   * 发送消息到主应用
   */
  sendToMainApp(type: string, payload: any) {
    const message: MessageData = {
      type,
      payload,
      source: this.source,
      target: "main-app",
      timestamp: Date.now(),
    };

    // 发送到父窗口
    if (window.parent !== window) {
      window.parent.postMessage(message, "*");
    }

    // 发送到当前窗口（用于测试）
    window.postMessage(message, "*");
  }

  /**
   * 监听来自主应用的消息
   */
  onMessage(type: string, callback: (payload: any) => void) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, []);
    }
    this.listeners.get(type)!.push(callback);
  }

  /**
   * 移除消息监听器
   */
  offMessage(type: string, callback?: (payload: any) => void) {
    if (!this.listeners.has(type)) return;

    if (callback) {
      const callbacks = this.listeners.get(type)!;
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    } else {
      this.listeners.delete(type);
    }
  }

  /**
   * 游戏登录
   */
  loginToGame(gameData: GameLoginData) {
    this.sendToMainApp("GAME_LOGIN", gameData);
  }

  /**
   * 导航到指定路径
   */
  navigateTo(navigationData: NavigationData) {
    this.sendToMainApp("NAVIGATE", navigationData);
  }

  /**
   * 更新用户信息
   */
  updateUser(userData: UserData) {
    this.sendToMainApp("USER_UPDATE", userData);
  }

  /**
   * 显示通知
   */
  showNotification(
    message: string,
    type: "success" | "error" | "info" = "info"
  ) {
    this.sendToMainApp("SHOW_NOTIFICATION", { message, type });
  }

  /**
   * 打开充值页面
   */
  openDeposit() {
    this.sendToMainApp("OPEN_DEPOSIT", {});
  }

  /**
   * 打开提现页面
   */
  openWithdraw() {
    this.sendToMainApp("OPEN_WITHDRAW", {});
  }

  /**
   * 打开客服
   */
  openSupport() {
    this.sendToMainApp("OPEN_SUPPORT", {});
  }

  /**
   * 切换主题
   */
  changeTheme(theme: "light" | "dark" | "brand") {
    this.sendToMainApp("CHANGE_THEME", { theme });
  }

  /**
   * 切换语言
   */
  changeLanguage(language: string) {
    this.sendToMainApp("CHANGE_LANGUAGE", { language });
  }

  /**
   * 获取用户信息
   */
  getUserInfo() {
    this.sendToMainApp("GET_USER_INFO", {});
  }

  /**
   * 获取余额
   */
  getBalance() {
    this.sendToMainApp("GET_BALANCE", {});
  }

  /**
   * 刷新页面数据
   */
  refreshData() {
    this.sendToMainApp("REFRESH_DATA", {});
  }

  /**
   * 清理资源
   */
  destroy() {
    window.removeEventListener("message", this.handleMessage);
    window.removeEventListener("message", this.handleParentMessage);
    this.listeners.clear();
  }
}

// 创建全局通信实例
export const mobileCommunication = new MobileCommunication();

// 导出便捷方法
export const {
  loginToGame,
  navigateTo,
  updateUser,
  showNotification,
  openDeposit,
  openWithdraw,
  openSupport,
  changeTheme,
  changeLanguage,
  getUserInfo,
  getBalance,
  refreshData,
  onMessage,
  offMessage,
} = mobileCommunication;
